import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { default as languageStrings } from '../../global/l10n/PartMatches.json';
import Util from '../../global/util';
import { PartMatch, PartMatchGroup } from './part-matches.interface';
import PartMatchDao from './part-matches.dao';

@Component({
    tag: 'ti-part-matches',
    styleUrl: 'part-matches.scss',
    shadow: true
})
export class PartMatches {

    private _collapseActionLabel: string;
    private _commonQueryString: string = 'keyMatch={searchTerm}&tisearch={tisearch}&usecase=partmatches';
    private _expandActionLabel: string;
    private _isExpanded: boolean;
    private _partMatchCount: number;
    private _rowsToDisplay: number;
    private _titleLabel: string;
    private _viewAllLabel: string;

    /**
     * Load Statuses
     */
    private _statusType = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    }

    /**
     * The element for this component
     */
    @Element() _hostElement: HTMLTiPartMatchesElement;

    /**
     * Holds gpn response data
     *
     * @type {PartMatch}
     */
    @State() partMatchDetails: PartMatch[];

    /**
     * Holds gpn response data
     *
     * @type {PartMatchGroup[]}
     */
    @State() partMatchGroups: PartMatchGroup[];

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @type {string}
     * @default _statusType.Loading
     */
    @State() status: string = this._statusType.LOADING;
    @Watch('status')
    handleComponentStatusUpdate(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue === this._statusType.READY) {
            this.componentRendered.emit({
                tag: this._hostElement.tagName
            });
        }
    }

    /**
     * Number of data rows to show in "minimize"d state
     * @type {number}
     * @default 3
     */
    @Prop() collapsedRows: number = 3;

    /**
     * Value of locale
     *
     * @type {string}
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';

    /**
     * Number of data rows to show in "view all" state
     * @type {number}
     * @default 25
     */
    @Prop() maxExpandedRows: number = 25;

    /**
     * Family name that should be listed first if found.
     *
     * @type {string}
     */
    @Prop() priorityFamilyName: string;

    /**
     * URL for the search api
     * @type {string}
     * @default '/search/partMatch'
     */
    @Prop() searchApiUrl: string = '/search/partMatch';

    /**
     * search term value. Used for metrics tracking.
     *
     * @type {string}
     */
    @Prop() searchString: string;

    /**
     * root URL for ti.com
     * @type {string}
     * @default 'https://www.ti.com'
     */
    @Prop() tiHost: string = 'https://www.ti.com';

    /**
     * Value for tisearch metrics parameter
     *
     * @type {string}
     * @default 'universal_search'
     */
    @Prop() tisearch: string = 'universal_search';

    /**
     * When part-matches appears with the gpn-snapshot component, a different
     * title is used. This flag is used to set whether the default title should
     * be displayed or the gpn-specific title.
     *
     * @type {boolean}
     * @default false
     */
    @Prop() useGpnTitleLabel: boolean = false;
    @Watch('useGpnTitleLabel')
    handleUseGpnTitleLabel(newValue: boolean, oldValue: boolean) {
        if (newValue !== oldValue) {
            this.useGpnTitleLabel = newValue;
            this._titleLabel = this.useGpnTitleLabel
                    ? languageStrings[this.locale]['titleLabelGPN']
                    : languageStrings[this.locale]['titleLabelDefault'];
        }
    }

    /**
     * Catch link clicks and attach analytics query parameters
     * @param event
     */
    @Listen('click')
    clickHandler(event: any) {
        const tag = (event.composedPath && event.composedPath()[0]) || event.target as HTMLElement;
        if (tag.tagName.toLowerCase() === 'a') {
            event.preventDefault();
            const url = Util.formatUrl(
                tag.getAttribute('href'),
                {
                    searchTerm: this.searchString,
                    tisearch: this.tisearch.toLocaleLowerCase()
                },
                this._commonQueryString
            );
            window.location.href = url;
        }
    }

    /**
     * Method to set the PartMatch data
     */
    @Method()
    public async updateData(partMatchDetails: PartMatch[]) {
        if (partMatchDetails) {
            this.partMatchDetails = partMatchDetails;
            this._partMatchCount = Math.min(partMatchDetails.length, this.maxExpandedRows);
            this._isExpanded = false;
            this._rowsToDisplay = Math.min(this._isExpanded ? this.maxExpandedRows : this.collapsedRows, this._partMatchCount);
            this._viewAllLabel = this._isExpanded
                    ? this._collapseActionLabel
                    : this._expandActionLabel.replace('{0}', this._partMatchCount.toString());
            this.partMatchGroups = PartMatchDao.groupPartMatches(partMatchDetails.slice(0, this._rowsToDisplay));
            this.partMatchGroups = PartMatchDao.prioritizeFamilyPartMatches(this.priorityFamilyName, this.partMatchGroups)

            this.status = this._statusType.READY;
        }
        else {
            this.status = this._statusType.ERROR;
        }
    }

    /**
     * Event for when component section has rendered
     */
    @Event() componentRendered: EventEmitter;

    componentWillLoad() {
        if (this.searchString) {
            this._updateData();
        }
        this._updateLanguageStrings();
    }

    private _handleViewAllClick() {
        this._isExpanded = !this._isExpanded;
        this._rowsToDisplay = Math.min(this._isExpanded ? this.maxExpandedRows : this.collapsedRows, this. _partMatchCount);
        this._viewAllLabel = this._isExpanded
                ? this._collapseActionLabel
                : this._expandActionLabel.replace('{0}', this._partMatchCount.toString());
        this.partMatchGroups = PartMatchDao.groupPartMatches(this.partMatchDetails.slice(0, this._rowsToDisplay));
        this.partMatchGroups = PartMatchDao.prioritizeFamilyPartMatches(this.priorityFamilyName, this.partMatchGroups)
    }

    private async _updateData() {
        this.updateData(await PartMatchDao.searchPartMatches(this.searchString, this.locale, this.searchApiUrl, this.tiHost));
    }

    /**
     * Function to update language.
     */
    private _updateLanguageStrings() {
        this.locale = Util.getValidLocale(this.locale);

        this._collapseActionLabel = languageStrings[this.locale]['collapseActionLabel'];
        this._expandActionLabel = languageStrings[this.locale]['expandActionLabel'];
        this._titleLabel = this.useGpnTitleLabel ?
                languageStrings[this.locale]['titleLabelGPN'] :
                languageStrings[this.locale]['titleLabelDefault'];
    }

    render() {
        if (this.status !== this._statusType.READY || !this.partMatchDetails.length) {
            return '';
        }
        return (
            <Host>
                <ti-card appearance="plain-white">
                <div class='ti-part-matches'>
                        <h6>{this._titleLabel}</h6>
                        {this.partMatchGroups.map((group) =>
                            <div class="ti-part-matches-family">
                                {group.isDevice ?
                                    <a class="ti-part-matches-label" href={group.url} innerHTML={group.group}></a> :
                                    <span class="ti-part-matches-label" innerHTML={group.group}></span>
                                }
                                <div class="ti-part-matches-list">
                                    {group.matches.map((resource) =>
                                        <div class="ti-part-matches-list-row">
                                            <span class='ti-part-matches-number'>
                                                <a href={`${this.tiHost}${resource.folderUrl}`}>{resource.partNumber}</a>
                                            </span>
                                            <span class='ti-part-matches-description' innerHTML= {resource.description}></span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {(this._partMatchCount > this.collapsedRows) ?
                            <div class="ti-pagination-row">
                                <div class="ti-pagination-more">
                                    <span class={`ti-part-matches-expander ${this._isExpanded ? 'expanded' : 'collapsed'}`}
                                        onClick={() => this._handleViewAllClick()}>
                                        {this._viewAllLabel}
                                        <ti-svg-icon appearance="secondary" size="s" icon-set="objects">chevron-up</ti-svg-icon>
                                    </span>
                                </div>
                            </div>
                            : ''
                        }
                    </div>
                </ti-card>
            </Host>
        )
    }

}
